import { translate } from "@/plugins/i18n";
import BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";

export class EnumBmEntityType implements BaseEnumType {
  private static allValues: EnumBmEntityType[] = []; // make sure this is top-most
  
  public static none = new EnumBmEntityType(0, "None", "enum_bm_entity_type_none");
  public static outlet = new EnumBmEntityType(1, "Outlet", "enum_bm_entity_type_outlet");
  public static order = new EnumBmEntityType(2, "Order", "enum_bm_entity_type_order");
  public static credit = new EnumBmEntityType(3, "Credit", "enum_bm_entity_type_credit");
  public static bmTask = new EnumBmEntityType(4, "BmTask", "enum_bm_entity_type_bm_task");
  public static communicationLogEntry = new EnumBmEntityType(5, "CommunicationLogEntry", "enum_bm_entity_type_communication_log_entry");
  public static assembly = new EnumBmEntityType(6, "Assembly", "enum_bm_entity_type_assembly");
  public static assemblyProcedure = new EnumBmEntityType(7, "AssemblyProcedure", "enum_bm_entity_type_assembly_procedure");
  public static supplier = new EnumBmEntityType(8, "Supplier", "enum_bm_entity_type_supplier");
  public static outletGroup = new EnumBmEntityType(9, "OutletGroup", "enum_bm_entity_type_outlet_group");
  public static purchaseOrder = new EnumBmEntityType(10, "PurchaseOrder", "enum_bm_entity_type_purchase_order");
  public static product = new EnumBmEntityType(11, "Product", "enum_bm_entity_type_product");
  public static material = new EnumBmEntityType(12, "Material", "enum_bm_entity_type_material");
  public static stockTake = new EnumBmEntityType(13, "StockTake", "enum_bm_entity_type_stock_take");
  public static stockTransfer = new EnumBmEntityType(14, "StockTransfer", "enum_bm_entity_type_stock_transfer");
  public static stockAdjustment = new EnumBmEntityType(15, "StockAdjustment", "enum_bm_entity_type_stock_adjustment");
  public static ullage = new EnumBmEntityType(16, "Ullage", "enum_bm_entity_type_ullage");
  public static priceList = new EnumBmEntityType(17, "PriceList", "enum_bm_entity_type_price_list");
  public static lead = new EnumBmEntityType(18, "Lead", "enum_bm_entity_type_lead");
  public static leadCommunicationLogEntry = new EnumBmEntityType(19, "LeadCommunicationLogEntry", "enum_bm_entity_type_lead_communication_log_entry");
  public static vessel = new EnumBmEntityType(20, "Vessel", "enum_bm_entity_type_vessel");
  public static pallet = new EnumBmEntityType(21, "Pallet", "enum_bm_entity_type_pallet");
  public static batch = new EnumBmEntityType(22, "Batch", "enum_bm_entity_type_batch");
  public static batchProcessInstruction = new EnumBmEntityType(23, "BatchProcessInstruction", "enum_bm_entity_type_batch_process_instruction");
  public static process = new EnumBmEntityType(24, "Process", "enum_bm_entity_type_process");
  public static tpSetting = new EnumBmEntityType(25, "TpSetting", "enum_bm_entity_type_tp_setting");
  public static supplierManufacturerCommunicationLogEntry = new EnumBmEntityType(26, "SupplierManufacturerCommunicationLogEntry", "enum_bm_entity_type_supplier_manufacturer_communication_log_entry");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumBmEntityType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isNone() { return this === EnumBmEntityType.none; }
  public isOutlet() { return this === EnumBmEntityType.outlet; }
  public isOrder() { return this === EnumBmEntityType.order; }
  public isCredit() { return this === EnumBmEntityType.credit; }
  public isBmTask() { return this === EnumBmEntityType.bmTask; }
  public isCommunicationLogEntry() { return this === EnumBmEntityType.communicationLogEntry; }
  public isAssembly() { return this === EnumBmEntityType.assembly; }
  public isAssemblyProcedure() { return this === EnumBmEntityType.assemblyProcedure; }
  public isSupplier() { return this === EnumBmEntityType.supplier; }
  public isOutletGroup() { return this === EnumBmEntityType.outletGroup; }
  public isPurchaseOrder() { return this === EnumBmEntityType.purchaseOrder; }
  public isProduct() { return this === EnumBmEntityType.product; }
  public isMaterial() { return this === EnumBmEntityType.material; }
  public isStockTake() { return this === EnumBmEntityType.stockTake; }
  public isStockTransfer() { return this === EnumBmEntityType.stockTransfer; }
  public isStockAdjustment() { return this === EnumBmEntityType.stockAdjustment; }
  public isUllage() { return this === EnumBmEntityType.ullage; }
  public isPriceList() { return this === EnumBmEntityType.priceList; }
  public isLead() { return this === EnumBmEntityType.lead; }
  public isLeadCommunicationLogEntry() { return this === EnumBmEntityType.leadCommunicationLogEntry; }
  public isVessel() { return this === EnumBmEntityType.vessel; }
  public isPallet() { return this === EnumBmEntityType.pallet; }
  public isBatch() { return this === EnumBmEntityType.batch; }
  public isBatchProcessInstruction() { return this === EnumBmEntityType.batchProcessInstruction; }
  public isProcess() { return this === EnumBmEntityType.process; }
  public isTpSetting() { return this === EnumBmEntityType.tpSetting; }
  public isSupplierManufacturerCommunicationLogEntry() { return this === EnumBmEntityType.supplierManufacturerCommunicationLogEntry; }

  public static getValues(): readonly EnumBmEntityType[] {
    return EnumBmEntityType.allValues;
  }

  public static getValuesExcept0(): readonly EnumBmEntityType[] {
    return EnumBmEntityType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumBmEntityType {
    const found = EnumBmEntityType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumBmEntityType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumBmEntityType): EnumBmEntityType {
    const found = name ? EnumBmEntityType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumBmEntityType - Value not found: ${ name }`);
    }

    return found;
  }
}